import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, InformationCircleIcon } from '@heroicons/react/24/solid';
import CSVPreviewModal from './CSVPreviewModal';

/**
 * DictionaryCard - Presentational component for displaying a data dictionary item
 * Shows upload/download status and provides actions for import/export
 */
function DictionaryCard({
  title,
  uploadedDate,
  canExport,
  canImport,
  onExport,
  onImport,
  onInfoClick,
  onPreviewClose,
  onPreviewCancel,
  onPreviewUpload,
  isExporting,
  isImporting,
  disabled = false,
}) {
  const [dragActive, setDragActive] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showFormatInfo, setShowFormatInfo] = useState(false);
  const fileInputRef = useRef(null);
  const formatInfoRef = useRef(null);

  // Close info tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (formatInfoRef.current && !formatInfoRef.current.contains(event.target)) {
        setShowFormatInfo(false);
      }
    };

    if (showFormatInfo) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showFormatInfo]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelected(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelected(file);
    }
  };

  const handleFileSelected = (file) => {
    setPreviewFile(file);
    setShowPreview(true);
  };

  const handlePreviewConfirm = async () => {
    if (previewFile) {
      onPreviewUpload?.();
      await onImport(previewFile);
      setShowPreview(false);
      setPreviewFile(null);
      resetFileInput();
    }
  };

  const handlePreviewCancel = () => {
    onPreviewCancel?.();
    setShowPreview(false);
    setPreviewFile(null);
    resetFileInput();
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Derive dictionary type from title
  const getDictionaryType = () => {
    const titleLower = title.toLowerCase();
    if (titleLower.includes('recipe')) return 'recipes';
    if (titleLower.includes('product')) return 'products';
    if (titleLower.includes('event') && !titleLower.includes('sub')) return 'events';
    if (titleLower.includes('sub')) return 'subevents';
    return 'recipes'; // default
  };

  // Format information for each dictionary type
  const formatInfo = {
    recipes: {
      header: 'code;market;name;extra',
      columns: 4,
      description: 'Recipes format (4 columns)',
    },
    products: {
      header: 'code;market;name;extra;dict_recipe_code',
      columns: 5,
      description: 'Products format (5 columns)',
    },
    events: {
      header: 'code;name;type;extra',
      columns: 4,
      description: 'Events format (4 columns)',
    },
    subevents: {
      header: 'code;name;event_code',
      columns: 3,
      description: 'Subevents format (3 columns)',
    },
  };

  const currentFormat = formatInfo[getDictionaryType()];

  return (
    <>
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        {/* Header with Title and Info Icon */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <div className="relative" ref={formatInfoRef}>
            <button
              onClick={() => {
                onInfoClick?.();
                setShowFormatInfo(!showFormatInfo);
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Show format information"
            >
              <InformationCircleIcon className="h-6 w-6" />
            </button>
            
            {/* Tooltip */}
            {showFormatInfo && (
              <div className="absolute right-0 mt-2 w-80 bg-gray-900 text-white text-sm rounded-lg shadow-lg p-4 z-10">
                <p className="font-semibold mb-2">{currentFormat.description}</p>
                <div className="bg-gray-800 rounded p-3 mb-2">
                  <p className="font-mono text-xs break-words">{currentFormat.header}</p>
                </div>
                <p className="text-gray-300 text-xs">
                  Expected: <span className="font-semibold">{currentFormat.columns} columns</span> separated by semicolons (;)
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Upload Status */}
        <div className="mb-4">
          {uploadedDate ? (
            <p className="text-sm text-gray-600">
              Uploaded on{' '}
              <span className="font-medium">
                {new Date(uploadedDate).toLocaleString()}
              </span>
            </p>
          ) : (
            <p className="text-sm text-gray-500">This dictionary has not been uploaded yet.</p>
          )}
        </div>

        {/* Export Button */}
        {canExport && (
          <div className="mb-4">
            <button
              onClick={onExport}
              disabled={isExporting || disabled}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              {isExporting ? 'Downloading...' : 'Download CSV'}
            </button>
          </div>
        )}

        {/* Import Section */}
        {canImport && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              Upload an updated Data Dictionary
            </p>
            <label
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`block border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                dragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400 bg-gray-50'
              } ${disabled || isImporting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                disabled={isImporting || disabled}
                className="hidden"
              />
              <div className="flex justify-center mb-2">
                <ArrowUpTrayIcon className="h-8 w-8 text-blue-500" />
              </div>
              <div>
                <p className="text-blue-600 font-medium">
                  {isImporting ? 'Uploading...' : 'Click to upload'}
                </p>
                <p className="text-gray-500 text-sm">or drag and drop.</p>
                <p className="text-gray-500 text-xs mt-1">Upload a valid CSV file</p>
              </div>
            </label>
          </div>
        )}

        {/* No Permissions Message */}
        {!canExport && !canImport && (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
            <p className="text-sm text-yellow-800">
              You do not have permission to access this dictionary.
            </p>
          </div>
        )}
      </div>

      {/* CSV Preview Modal */}
      <CSVPreviewModal
        isOpen={showPreview}
        file={previewFile}
        title={`Preview - ${title}`}
        onConfirm={handlePreviewConfirm}
        onCancel={handlePreviewCancel}
        onClose={onPreviewClose}
        isLoading={isImporting}
        dictionaryType={getDictionaryType()}
      />
    </>
  );
}

DictionaryCard.propTypes = {
  title: PropTypes.string.isRequired,
  uploadedDate: PropTypes.string,
  canExport: PropTypes.bool.isRequired,
  canImport: PropTypes.bool.isRequired,
  onExport: PropTypes.func.isRequired,
  onImport: PropTypes.func.isRequired,
  onInfoClick: PropTypes.func,
  onPreviewClose: PropTypes.func,
  onPreviewCancel: PropTypes.func,
  onPreviewUpload: PropTypes.func,
  isExporting: PropTypes.bool,
  isImporting: PropTypes.bool,
  disabled: PropTypes.bool,
};

DictionaryCard.defaultProps = {
  uploadedDate: null,
  onInfoClick: null,
  onPreviewClose: null,
  onPreviewCancel: null,
  onPreviewUpload: null,
  isExporting: false,
  isImporting: false,
  disabled: false,
};

export default DictionaryCard;
